/**
 * SpirometryComparisonViewer
 *
 * Compares a spirometry measure *over time* across subjects. For each selected
 * subject, one line traces the trajectory of the chosen measure (FEV1, FVC, PEF
 * or FEV1/FVC) across their visits, so decline or improvement is directly
 * readable off the chart.
 *
 *   x-axis = visit date (chronological)
 *   y-axis = the selected measure's value
 *   one line + markers per subject
 *
 * This replaces the earlier flow-volume-loop overlay, which plotted volume vs.
 * flow for a single aggregated effort and therefore showed no time dimension.
 *
 * Data-fetch strategy
 * ───────────────────
 * 1. useSpirometryEffortsQuery() → all efforts; grouped into per-subject visits
 *    (one visit per transmittal_number, carrying its collection_date).
 * 2. One <VisitMeasureLoader> per (subject, visit) — the same child-loader
 *    pattern EcgVisitTrends uses for a variable number of visits — fetches that
 *    visit's curves and reports its best-of-visit measures up.
 * 3. Measures are assembled per subject, sorted by date, into one trajectory
 *    line each.
 *
 * The measure derivation lives in `utils/spirometryStats` (pure + unit-tested).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Select, Spin, Alert, Empty } from "antd";
import dayjs from "dayjs";
import { curveLinear, line, scaleLinear } from "d3";

import {
  useSpirometryCurvesQuery,
  useSpirometryEffortsQuery,
} from "@/hooks/useWaveformQueries";
import {
  bestVisitMeasures,
  type VisitMeasures,
} from "../utils/spirometryStats";

// ─── Constants ───────────────────────────────────────────────────────────────

const MAX_SUBJECTS = 8;

/** 10 perceptually distinct colors for subject trajectories. */
const SUBJECT_COLORS = [
  "#1890ff",
  "#fa541c",
  "#52c41a",
  "#722ed1",
  "#faad14",
  "#13c2c2",
  "#eb2f96",
  "#a0d911",
  "#f5222d",
  "#2f54eb",
] as const;

const MARGIN = { top: 16, right: 24, bottom: 44, left: 56 };
const CHART_HEIGHT = 380;
const MIN_WIDTH = 320;
const DOMAIN_PAD = 0.05;
const AXIS_COLOR = "#9ca3af";
const TICK_COLOR = "#6b7280";
const LABEL_COLOR = "#374151";
const MARKER_RADIUS = 3.5;
const MAX_DATE_TICKS = 8;

type MeasureKey = keyof VisitMeasures;

interface MeasureOption {
  value: MeasureKey;
  label: string;
  unit: string;
}

const MEASURES: MeasureOption[] = [
  { value: "fev1", label: "FEV1", unit: "L" },
  { value: "fvc", label: "FVC", unit: "L" },
  { value: "pef", label: "PEF", unit: "L/s" },
  { value: "fev1FvcRatio", label: "FEV1/FVC", unit: "%" },
];

// ─── Types ────────────────────────────────────────────────────────────────────

/** One visit for one subject (a transmittal submitted on a collection date). */
interface VisitRef {
  subjectId: string;
  transmittalNumber: number;
  date: string;
}

/** A visit's best-of measures, reported up once its curves have loaded. */
interface VisitPoint extends VisitRef {
  measures: VisitMeasures;
}

/** A subject's ordered trajectory for the selected measure. */
interface TrajectorySeries {
  subjectId: string;
  color: string;
  points: Array<{ epochMs: number; value: number; date: string }>;
}

// ─── VisitMeasureLoader ───────────────────────────────────────────────────────

/**
 * Loads one visit's spirometry curves and reports its best-of-visit measures up.
 * Renders nothing. Reporting all measures at once (rather than the selected one)
 * means switching the measure selector never triggers a refetch.
 */
function VisitMeasureLoader({
  visit,
  onPoint,
}: {
  visit: VisitRef;
  onPoint: (pointKey: string, point: VisitPoint) => void;
}) {
  const { data } = useSpirometryCurvesQuery({
    transmittalNumber: visit.transmittalNumber,
  });

  useEffect(() => {
    if (!data) return;
    const measures = bestVisitMeasures(data.efforts);
    if (!measures) return;
    onPoint(`${visit.subjectId}::${visit.transmittalNumber}`, {
      ...visit,
      measures,
    });
  }, [data, visit, onPoint]);

  return null;
}

// ─── TrajectoryChart ──────────────────────────────────────────────────────────

/** Computes a padded [min, max] domain over the provided values. */
function paddedExtent(values: number[]): [number, number] {
  if (!values.length) return [0, 1];
  let min = Infinity;
  let max = -Infinity;
  for (const v of values) {
    if (v < min) min = v;
    if (v > max) max = v;
  }
  const span = max - min || Math.abs(max) || 1;
  const pad = span * DOMAIN_PAD;
  return [min - pad, max + pad];
}

interface TrajectoryChartProps {
  series: TrajectorySeries[];
  yLabel: string;
}

const TrajectoryChart = ({ series, yLabel }: TrajectoryChartProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(MIN_WIDTH * 2);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      setContainerWidth(Math.max(entry.contentRect.width, MIN_WIDTH));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const innerWidth = Math.max(containerWidth - MARGIN.left - MARGIN.right, 10);
  const innerHeight = Math.max(CHART_HEIGHT - MARGIN.top - MARGIN.bottom, 10);

  const { xScale, yScale } = useMemo(() => {
    const allX = series.flatMap((s) => s.points.map((p) => p.epochMs));
    const allY = series.flatMap((s) => s.points.map((p) => p.value));
    const xs = scaleLinear().domain(paddedExtent(allX)).range([0, innerWidth]);
    // Physiologic measures are non-negative; keep the axis from dipping below 0.
    const [yLo, yHi] = paddedExtent(allY);
    const ys = scaleLinear()
      .domain([Math.max(0, yLo), yHi])
      .range([innerHeight, 0]);
    return { xScale: xs, yScale: ys };
  }, [series, innerWidth, innerHeight]);

  const lineGenerator = useMemo(
    () =>
      line<{ epochMs: number; value: number }>()
        .x((p) => xScale(p.epochMs))
        .y((p) => yScale(p.value))
        .curve(curveLinear),
    [xScale, yScale],
  );

  // X ticks: one per distinct visit date when there are few enough to be legible,
  // otherwise fall back to evenly-spaced scale ticks. Both are rendered as dates.
  const xTicks = useMemo(() => {
    const distinct = Array.from(
      new Set(series.flatMap((s) => s.points.map((p) => p.epochMs))),
    ).sort((a, b) => a - b);
    return distinct.length > 0 && distinct.length <= MAX_DATE_TICKS
      ? distinct
      : xScale.ticks(6);
  }, [series, xScale]);

  const yTicks = yScale.ticks(6);

  return (
    <div ref={containerRef} className="w-full">
      <svg width={containerWidth} height={CHART_HEIGHT} className="block">
        <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
          {/* Subject trajectories */}
          {series.map((s) => (
            <g key={s.subjectId}>
              <path
                d={lineGenerator(s.points) ?? ""}
                fill="none"
                stroke={s.color}
                strokeWidth={2}
              />
              {s.points.map((p) => (
                <circle
                  key={p.epochMs}
                  cx={xScale(p.epochMs)}
                  cy={yScale(p.value)}
                  r={MARKER_RADIUS}
                  fill={s.color}
                >
                  <title>
                    {`${s.subjectId} · ${dayjs(p.date).format("MMM D, YYYY")} · ${p.value.toFixed(2)}`}
                  </title>
                </circle>
              ))}
            </g>
          ))}

          {/* X axis */}
          <g transform={`translate(0,${innerHeight})`}>
            <line x1={0} x2={innerWidth} stroke={AXIS_COLOR} />
            {xTicks.map((tick) => (
              <g key={tick} transform={`translate(${xScale(tick)},0)`}>
                <line y2={4} stroke={AXIS_COLOR} />
                <text
                  y={16}
                  textAnchor="middle"
                  fontSize={10}
                  fill={TICK_COLOR}
                >
                  {dayjs(tick).format("MMM D")}
                </text>
              </g>
            ))}
            <text
              x={innerWidth / 2}
              y={38}
              textAnchor="middle"
              fontSize={11}
              fill={LABEL_COLOR}
            >
              Visit date
            </text>
          </g>

          {/* Y axis */}
          <g>
            <line y1={0} y2={innerHeight} stroke={AXIS_COLOR} />
            {yTicks.map((tick) => (
              <g key={tick} transform={`translate(0,${yScale(tick)})`}>
                <line x2={-4} stroke={AXIS_COLOR} />
                <text
                  x={-8}
                  textAnchor="end"
                  dominantBaseline="middle"
                  fontSize={10}
                  fill={TICK_COLOR}
                >
                  {tick}
                </text>
              </g>
            ))}
            <text
              transform={`translate(-44,${innerHeight / 2}) rotate(-90)`}
              textAnchor="middle"
              fontSize={11}
              fill={LABEL_COLOR}
            >
              {yLabel}
            </text>
          </g>

          {/* Axis border */}
          <rect
            x={0}
            y={0}
            width={innerWidth}
            height={innerHeight}
            fill="none"
            stroke={AXIS_COLOR}
            strokeOpacity={0.3}
          />
        </g>
      </svg>
    </div>
  );
};

// ─── SpirometryComparisonViewer ───────────────────────────────────────────────

export interface SpirometryComparisonViewerProps {
  /**
   * NCT ID of the trial — passed for caller consistency.
   * Queries resolve trial from URL routing context via useFetch.
   */
  nctId: string;
  /** Initial subject ID list shown in the multi-select. */
  subjectIds: string[];
}

export const SpirometryComparisonViewer = ({
  subjectIds: initialSubjectIds,
}: SpirometryComparisonViewerProps) => {
  // All efforts — populates the subject selector and each subject's visit list.
  const allEffortsQuery = useSpirometryEffortsQuery();
  const efforts = useMemo(
    () => allEffortsQuery.data?.efforts ?? [],
    [allEffortsQuery.data],
  );

  const allSubjectIds: string[] = useMemo(() => {
    const ids = efforts
      .map((e) => e.subject_id)
      .filter((id): id is string => Boolean(id));
    return [...new Set(ids)].sort();
  }, [efforts]);

  const selectOptions = allSubjectIds.map((id) => ({ label: id, value: id }));

  const [selectedSubjects, setSelectedSubjects] = useState<string[]>(() =>
    initialSubjectIds.slice(0, MAX_SUBJECTS),
  );
  const [measure, setMeasure] = useState<MeasureKey>("fev1");
  const measureOption =
    MEASURES.find((m) => m.value === measure) ?? MEASURES[0];

  // Per selected subject → chronological list of visits (one per transmittal).
  const visits: VisitRef[] = useMemo(() => {
    const selected = new Set(selectedSubjects);
    const seen = new Set<string>();
    const refs: VisitRef[] = [];
    for (const e of efforts) {
      const subjectId = e.subject_id;
      if (!subjectId || !selected.has(subjectId)) continue;
      const key = `${subjectId}::${e.transmittal_number}`;
      if (seen.has(key)) continue;
      seen.add(key);
      refs.push({
        subjectId,
        transmittalNumber: e.transmittal_number,
        date: e.collection_date,
      });
    }
    return refs;
  }, [efforts, selectedSubjects]);

  // Best-of-visit measures accumulate keyed by subject+transmittal, so switching
  // measure or subject never leaves stale points on the chart.
  const [pointMap, setPointMap] = useState<Record<string, VisitPoint>>({});
  const onPoint = useCallback(
    (pointKey: string, point: VisitPoint) =>
      setPointMap((prev) => ({ ...prev, [pointKey]: point })),
    [],
  );

  // Build one trajectory line per selected subject for the chosen measure.
  const series: TrajectorySeries[] = useMemo(() => {
    return selectedSubjects
      .slice(0, MAX_SUBJECTS)
      .map((subjectId, i) => {
        const points = visits
          .filter((v) => v.subjectId === subjectId)
          .map((v) => pointMap[`${subjectId}::${v.transmittalNumber}`])
          .filter((p): p is VisitPoint => Boolean(p))
          .map((p) => ({
            epochMs: dayjs(p.date).valueOf(),
            value: p.measures[measure],
            date: p.date,
          }))
          .filter((p) => Number.isFinite(p.epochMs) && Number.isFinite(p.value))
          .sort((a, b) => a.epochMs - b.epochMs);
        return {
          subjectId,
          color: SUBJECT_COLORS[i % SUBJECT_COLORS.length],
          points,
        };
      })
      .filter((s) => s.points.length > 0);
  }, [selectedSubjects, visits, pointMap, measure]);

  const isLoading = allEffortsQuery.isLoading;
  const error = allEffortsQuery.error;

  // Visits still waiting on their curve queries (points not yet reported).
  const pendingVisits = visits.filter(
    (v) => !pointMap[`${v.subjectId}::${v.transmittalNumber}`],
  ).length;

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[240px]">
          <p className="text-xs font-semibold text-gray-600 mb-1">
            Subjects to compare (max {MAX_SUBJECTS})
          </p>
          <Select
            mode="multiple"
            allowClear
            style={{ width: "100%" }}
            placeholder={
              allEffortsQuery.isLoading
                ? "Loading subjects…"
                : "Select subjects to compare"
            }
            options={selectOptions}
            value={selectedSubjects}
            onChange={(vals: string[]) =>
              setSelectedSubjects(vals.slice(0, MAX_SUBJECTS))
            }
            maxCount={MAX_SUBJECTS}
            loading={allEffortsQuery.isLoading}
          />
        </div>
        <div>
          <p className="text-xs font-semibold text-gray-600 mb-1">Measure</p>
          <Select<MeasureKey>
            style={{ width: 180 }}
            value={measure}
            onChange={setMeasure}
            options={MEASURES.map((m) => ({
              label: `${m.label} (${m.unit})`,
              value: m.value,
            }))}
          />
        </div>
      </div>

      {/* Curve loaders — one per (subject, visit); render nothing, feed points up. */}
      {visits.map((v) => (
        <VisitMeasureLoader
          key={`${v.subjectId}::${v.transmittalNumber}`}
          visit={v}
          onPoint={onPoint}
        />
      ))}

      {/* Loading / error / empty states */}
      {isLoading && (
        <div className="flex justify-center py-4">
          <Spin tip="Loading spirometry data…" />
        </div>
      )}

      {error != null && (
        <Alert
          type="warning"
          showIcon
          message="Spirometry data could not be loaded"
          description="The waveform service may be temporarily unreachable. Please try again later."
        />
      )}

      {!isLoading && error == null && selectedSubjects.length === 0 && (
        <Empty description="Select one or more subjects to compare their trajectory over time." />
      )}

      {!isLoading &&
        error == null &&
        selectedSubjects.length > 0 &&
        series.length === 0 &&
        pendingVisits === 0 && (
          <Empty description="No spirometry measures found for the selected subjects." />
        )}

      {/* Chart */}
      {series.length > 0 && (
        <>
          {/* Legend */}
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {series.map((s) => (
              <span
                key={s.subjectId}
                className="inline-flex items-center gap-1.5 text-sm"
              >
                <span
                  className="inline-block w-4 h-[2.5px] rounded"
                  style={{ background: s.color }}
                />
                {s.subjectId}
              </span>
            ))}
          </div>

          <TrajectoryChart
            series={series}
            yLabel={`${measureOption.label} (${measureOption.unit})`}
          />

          {pendingVisits > 0 && (
            <p className="text-xs text-gray-400">
              Loading {pendingVisits} more visit
              {pendingVisits === 1 ? "" : "s"}…
            </p>
          )}
        </>
      )}
    </div>
  );
};
